import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { Action, AuthType, RuntimeFilterOp, init } from '@thoughtspot/visual-embed-sdk';
import { LiveboardEmbed } from '@thoughtspot/visual-embed-sdk/react';
import { IconButton, Skeleton, Typography } from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import { useDispatch, useSelector } from 'react-redux';
import { getAccessToken } from 'src/routes/AuthContext';
import { Box } from '@mui/system';
import { getContractCountAction } from 'src/redux/actions/getContractCountAction';

const fetchData = async () => {
  const __ttData = await getAccessToken();
  //provider portal token
  //realms
  let realm = '';
  if (window.location.hostname === 'localhost') {
    realm = localStorage.getItem('realm') || '';
  } else {
    realm = window.location.hostname.split('-')[0] || '';
  }
  // TO DO:
  // The API host variable would remain the same but the variable for TS_USER should be coming from the user's realm once embedded in Insights

  if (realm && __ttData) {
    const response = await fetch(
      `https://ts.production-insights.restoreskills.com/thoughtspot/auth/${realm}`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${__ttData}`,
          'Content-Type': 'application/json',
        },
      }
    );
    const data = await response.json();
    return data;
  } else return '';
};

const initThoughtSpot = (data: any) => {
  let realm = '';
  if (window.location.hostname === 'localhost') {
    realm = localStorage.getItem('realm') || '';
  } else {
    realm = window.location.hostname.split('-')[0] || '';
  }
  // const __ttData = await getAccessToken();
  init({
    thoughtSpotHost: 'https://restoreskills.thoughtspot.cloud',
    authType: AuthType.TrustedAuthToken,
    // TO DO:
    // This should similarly reflect the user's realm once embedded in Insights and not be an environment variable
    username: realm || '',
    getAuthToken: (): Promise<string> => {
      return data?.token;
    },
  });
};

const ThoughtSpotIntegration = ({ who = 'therapist', realm }: any) => {
  const [data, setData] = useState<any>(null);
  const [refresh, setRefresh] = useState(false);
  const dispatch = useDispatch();
  const [isAuthenticate, setIsAuthenticate] = useState(true);
  const therapistUserName = useSelector(
    (state: any) => state?.getTherapistProfileReducer?.response?.data?.userName
  );

  const facilityId = localStorage.getItem('facilityId');

  //Validating that the facility stored in local storage is one of the assigned facilities of the user
  const relevantFacilities = useSelector(
    (state: any) => state?.getFacilityDropDownReducer?.response?.data?.data
  );
  let validFacility = false;
  if (relevantFacilities && relevantFacilities.length > 0) {
    for (let i = 0; i < relevantFacilities.length; i++) {
      if (relevantFacilities[i].uuid === facilityId) {
        validFacility = true;
      }
    }
  }

  const contractCount = useSelector((state: any) => state.getContractCountReducer?.response?.data);

  const [bannerText, setBannerText] = useState(
    'In order to produce data in your profit and loss report, you need to configure your contracts in the Enterprise Portal.'
  );

  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    if (contractCount?.finalCount === 0 && contractCount?.inactiveCount === 0) {
      setBannerText(
        'In order to produce data in your profit and loss report, you need to configure your contracts in the Enterprise Portal.'
      );
      setShowBanner(true);
    } else if (contractCount?.finalCount === 0 && contractCount?.inactiveCount > 0) {
      setBannerText(
        'All of your contracts have terminated. In order to produce data in your profit and loss and margin reports, you need to configure new contracts in the Enterprise Portal.'
      );
      setShowBanner(true);
    }
  }, [contractCount]);

  useEffect(() => {
    const fetchToken = async () => {
      try {
        const data = await fetchData();
        if (data?.detail === 'Could not validate credentials') setIsAuthenticate(false);
        setData(data);
        initThoughtSpot(data);
      } catch (error) {
        setIsAuthenticate(false);
      }
    };
    fetchToken();
    dispatch(getContractCountAction());
  }, []);

  // useEffect(() => {
  //   // if (!data?.token) return;
  //   init({
  //     thoughtSpotHost: 'https://restoreskills.thoughtspot.cloud',
  //     authType: AuthType.TrustedAuthToken,
  //     // TO DO:
  //     // This should similarly reflect the user's realm once embedded in Insights and not be an environment variable
  //     username: realm || '',
  //     getAuthToken: (): Promise<string> => {
  //       return data?.token;
  //     },
  //   });
  // }, [data]);

  const runtimeFilters =
    who === 'therapist'
      ? [
          {
            columnName: 'User Name',
            operator: RuntimeFilterOp.EQ,
            // TO DO:
            // The value below will need to be a parameter passed in based on who is logged in once embedded in Insights, not hardcoded
            values: therapistUserName ? [therapistUserName] : ['helpy123'],
          },
        ]
      : [];

  if (who === 'therapist' || who === 'dor') {
    if (validFacility) {
      runtimeFilters.push({
        columnName: 'Facility ID',
        operator: RuntimeFilterOp.EQ,
        values: [facilityId],
      });
    } else {
      return <Typography variant="h5">No Facility Selected</Typography>;
    }
  }

  return (
    <>
      {showBanner && (
        <Box
          sx={{
            display: 'flex',
            border: '1px solid #00000042',
            background: '#dc2626',
            minHeight: '60px',
            color: '#ffffff',
            padding: '5px 10px',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography
            sx={{
              wordBreak: 'break-word',
              whiteSpace: 'normal',
              fontSize: 18,
            }}
          >
            {bannerText}
          </Typography>

          <IconButton
            onClick={() => {
              setShowBanner(false);
            }}
          >
            <CancelIcon sx={{ color: 'white' }} />
          </IconButton>
        </Box>
      )}

      {data && data?.parameter_values ? (
        <LiveboardEmbed
          frameParams={{
            width: '100%',
            height: '90vh',
          }}
          // fullHeight={true}
          // This gets the first value from the Thoughtspot Auth Service parameter_values and uses it as the liveboard id since the Ids have been the same.
          // It may need to be updated once we determine how the liveboards will be displayed
          liveboardId={
            data?.parameter_values
              ? who === 'therapist'
                ? data?.parameter_values?.THERAPIST_REPORTING
                : data?.parameter_values?.DOR_REPORTING
              : ''
          }
          isLiveboardHeaderSticky={false}
          // visibleActions={[Action.DownloadAsPdf]}
          hideLiveboardHeader={false}
          hiddenActions={[
            Action.RequestAccess,
            Action.AddToFavorites,
            Action.Share,
            Action.Edit,
            Action.MakeACopy,
            Action.RenameModalTitleDescription,
            Action.Present,
            Action.Schedule,
            Action.SchedulesList,
            Action.ExportTML,
            Action.RequestVerification,
            Action.LiveboardInfo,
            Action.CreateMonitor,
            Action.Pin,
            Action.ShowUnderlyingData,
            Action.CopyLink,
            Action.ExportTML,
            Action.ImportTML,
            Action.UpdateTML,
            Action.EditTML,
            Action.SyncToOtherApps,
          ]}
          // onInit={(e) => !refresh && setRefresh(true)}
          runtimeFilters={runtimeFilters}
        />
      ) : !isAuthenticate ? null : (
        <>
          <Skeleton height={30} />
          <Skeleton
            height={30}
            animation="wave"
          />
          <Skeleton
            height={30}
            animation={false}
          />
        </>
      )}
    </>
  );
};

export default ThoughtSpotIntegration;